export const PRINTER_TYPE_OPTIONS = [
    { value: 'Network ESC/POS', label: 'Network' },
    { value: 'Windows Shared ESC/POS', label: 'Windows shared' }
] as const;

export type PrinterType = typeof PRINTER_TYPE_OPTIONS[number]['value'];

export const MEV_RECEIPT_API_KEY = 'MEV-RECEIPT-PRINTER'
export const DEFAULT_MEV_PRINTER_NAME = 'MEV printer'
export const DEFAULT_MEV_PRINTER_ID = 'mev-customer-receipt-printer'
export const KITCHEN_ALL_API_KEY = 'KITCHEN-ALL'
export const KITCHEN_ALL_PRINTER_NAME = 'Kitchen All'
export const KITCHEN_ALL_PRINTER_ID = 'kitchen-all'

export const isMevReceiptApiKey = (apiKey?: string | null) =>
  apiKey === MEV_RECEIPT_API_KEY || apiKey === 'MEV-CUSTOMER-RECEIPT'

export const maskPrinterApiKey = (apiKey?: string | null, visibleDigits = 4) => {
  const key = String(apiKey || '').trim()
  if (!key) return ''
  if (key.length <= visibleDigits) return key
  return `••••${key.slice(-visibleDigits)}`
}

export const isDefaultMevPrinter = (printer?: { api_key?: string; _id?: string } | null) => {
  return isMevReceiptApiKey(printer?.api_key) || printer?._id === DEFAULT_MEV_PRINTER_ID
}

export const isKitchenAllPrinter = (printer?: { api_key?: string; _id?: string } | null) => {
  return printer?.api_key === KITCHEN_ALL_API_KEY || printer?._id === KITCHEN_ALL_PRINTER_ID
}

const hasPrinterDevice = (printer?: PrinterItem | null) =>
  Boolean(
    String(printer?.windows_shared_name || '').trim() ||
    String(printer?.network_address || '').trim()
  )

const defaultMevPrinterScore = (printer: PrinterItem) => {
  let score = 0
  if (hasPrinterDevice(printer)) score += 100
  if (printer.isOnline) score += 10
  if (printer.name && printer.name.trim() && printer.name.trim() !== DEFAULT_MEV_PRINTER_NAME) score += 2
  if (printer._id === DEFAULT_MEV_PRINTER_ID) score += 1
  return score
}

export const dedupeDefaultMevPrinters = (printers: PrinterItem[]): PrinterItem[] => {
  const defaults: PrinterItem[] = []
  const others: PrinterItem[] = []
  printers.forEach((printer) => {
    if (isDefaultMevPrinter(printer)) defaults.push(printer)
    else others.push(printer)
  })
  if (defaults.length <= 1) return printers

  const winner = [...defaults].sort((left, right) => defaultMevPrinterScore(right) - defaultMevPrinterScore(left))[0]
  return [
    {
      ...winner,
      _id: DEFAULT_MEV_PRINTER_ID,
      api_key: MEV_RECEIPT_API_KEY,
      name: winner.name?.trim() || DEFAULT_MEV_PRINTER_NAME,
    },
    ...others,
  ]
}

export const isMevReceiptVirtualPrinter = (setting?: { id?: string; generalTab?: { apikey?: string; name?: string } } | null) => {
  if (!setting) return false
  return (
    isMevReceiptApiKey(setting.generalTab?.apikey) ||
    setting.id === 'mev-receipt-printer' ||
    setting.generalTab?.name === 'Cloud MEV Receipt Printer' ||
    setting.generalTab?.name === DEFAULT_MEV_PRINTER_NAME
  )
}

export const createKitchenAllPhysicalPrinter = (): PrinterItem => ({
  _id: KITCHEN_ALL_PRINTER_ID,
  name: KITCHEN_ALL_PRINTER_NAME,
  api_key: KITCHEN_ALL_API_KEY,
  print_qr_receipt: false,
  num_copies: 1,
  printer_type: 'Windows Shared ESC/POS',
  network_address: '',
  network_port: 9100,
  windows_shared_name: '',
  isOnline: false,
})

export const placeKitchenAllBesideMev = (printers: PrinterItem[]): PrinterItem[] => {
  const existing = printers.find(isKitchenAllPrinter)
  const kitchen: PrinterItem = {
    ...(existing || createKitchenAllPhysicalPrinter()),
    _id: KITCHEN_ALL_PRINTER_ID,
    name: KITCHEN_ALL_PRINTER_NAME,
    api_key: KITCHEN_ALL_API_KEY,
  }
  delete kitchen.stationId
  const rest = printers.filter((printer) => !isKitchenAllPrinter(printer))
  const mevIndex = rest.findIndex(isDefaultMevPrinter)
  const next = [...rest]
  next.splice(mevIndex >= 0 ? mevIndex + 1 : 0, 0, kitchen)
  return next
}

export const createDefaultMevPhysicalPrinter = (windowsSharedName = ''): PrinterItem => ({
  _id: DEFAULT_MEV_PRINTER_ID,
  name: DEFAULT_MEV_PRINTER_NAME,
  api_key: MEV_RECEIPT_API_KEY,
  print_qr_receipt: false,
  num_copies: 1,
  printer_type: 'Windows Shared ESC/POS',
  network_address: '',
  network_port: 9100,
  windows_shared_name: windowsSharedName,
  isOnline: false,
})

export interface PrinterItem {
    _id?: string;
    name: string;
    api_key: string;
    print_qr_receipt: boolean;
    num_copies: number;
    printer_type: PrinterType;
    network_address: string;
    network_port: number;
    windows_shared_name: string;
    command_language?: 'ESCPOS' | 'STARLINE' | 'STARGRAPHIC' | 'STARPRNT' | 'STARIMPACT';
    command_language_verified?: 'ESCPOS' | 'STARLINE' | 'STARGRAPHIC' | 'STARPRNT' | 'STARIMPACT';
    printer_model?: string;
    location?: string;
    stationId?: string;
    disabled?: boolean;
    isOnline: boolean;
    updatedAt?: string;
    createdAt?: string;
} 